/**
 * Iter 213 — SecurityMonitor : moniteur de sécurité côté client.
 *
 * Détecte (best-effort, contournable mais crée un signal) :
 *  - Ouverture des DevTools (window.outerWidth - innerWidth > 160px ou debugger timing > 100ms)
 *  - Console open suspect (window.console.firebug ou debugger profiling)
 *  - Mouvements de souris robotiques (cadence parfaite)
 *  - Collage automatisé (paste sans event souris/clavier précédent)
 *  - Burst de requêtes (intercepte fetch/axios — > 30 en 5s)
 *
 * Action : POST /api/security/alert avec `kind` + `severity` + `context`.
 * Bandeau d'avertissement visible si alerte HIGH/CRITICAL détectée.
 *
 * Monté dans App.js après l'auth.
 */
import { useEffect, useState, useRef } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { AlertTriangle, X } from "lucide-react";

const ALERT_DEDUPE_MS = 5 * 60 * 1000; // 5 minutes
const COOLDOWN_KEY = "blitzz_sec_cooldown_v1";

function loadCooldown() {
  try {
    const raw = localStorage.getItem(COOLDOWN_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function saveCooldown(map) {
  try {
    localStorage.setItem(COOLDOWN_KEY, JSON.stringify(map));
  } catch {
    /* noop */
  }
}

export default function SecurityMonitor() {
  const { axiosAuth, user } = useAuth();
  const [warning, setWarning] = useState(null);
  const cooldownRef = useRef(loadCooldown());
  const requestTimes = useRef([]);
  const lastInteraction = useRef(Date.now());
  const mouseTimings = useRef([]);

  const sendAlert = async (kind, severity = "medium", extraContext = {}) => {
    if (!user) return;
    // Dedupe per kind
    const now = Date.now();
    const last = cooldownRef.current[kind] || 0;
    if (now - last < ALERT_DEDUPE_MS) return;
    cooldownRef.current[kind] = now;
    saveCooldown(cooldownRef.current);

    try {
      const api = axiosAuth();
      await api.post("/security/alert", {
        kind,
        severity,
        url: window.location.pathname,
        context: {
          screen: `${window.screen?.width}x${window.screen?.height}`,
          inner: `${window.innerWidth}x${window.innerHeight}`,
          outer: `${window.outerWidth}x${window.outerHeight}`,
          ts: now,
          ...extraContext,
        },
      });
    } catch {
      // silently fail (le moniteur ne doit pas casser l'app)
    }

    if (severity === "high" || severity === "critical") {
      setWarning({ kind, severity });
    }
  };

  useEffect(() => {
    if (!user) return;

    // ── 1. Détection DevTools : timing du `debugger` statement ──────
    const checkDevTools = () => {
      const start = performance.now();
      // eslint-disable-next-line no-debugger
      // Note: debugger ne déclenche un break QUE si DevTools est ouvert
      // eslint-disable-next-line no-eval
      try { eval("debugger"); } catch { /* noop */ }
      const elapsed = performance.now() - start;
      if (elapsed > 100) {
        sendAlert("devtools_open", "medium", { method: "debugger_timing", elapsed_ms: Math.round(elapsed) });
      }
      // 2nd heuristique : différence outerWidth - innerWidth
      const dx = window.outerWidth - window.innerWidth;
      const dy = window.outerHeight - window.innerHeight;
      if (dx > 160 || dy > 200) {
        sendAlert("devtools_open", "medium", { method: "viewport_diff", dx, dy });
      }
    };

    // Run check every 30s + on focus
    const intDev = setInterval(checkDevTools, 30 * 1000);
    const onFocus = () => setTimeout(checkDevTools, 1500);
    window.addEventListener("focus", onFocus);
    setTimeout(checkDevTools, 3500); // initial check

    // ── 2. Console open : __defineGetter__ trick ───────────────────
    try {
      const detectorObj = {};
      Object.defineProperty(detectorObj, "consoleOpen", {
        get() {
          sendAlert("console_use", "medium", { method: "object_inspect" });
          return "🔒";
        },
      });
      // Quand DevTools inspecte cet objet → trigger
      // eslint-disable-next-line no-console
      console.debug?.("%c", "color: transparent", detectorObj);
    } catch {
      /* ignore */
    }

    // ── 3. Burst requests (intercept fetch) ─────────────────────────
    const origFetch = window.fetch;
    if (origFetch && !origFetch.__blitzz_wrapped) {
      const wrapped = async (...args) => {
        const url = typeof args[0] === "string" ? args[0] : args[0]?.url || "";
        if (url.includes("/api/")) {
          const t = Date.now();
          requestTimes.current.push(t);
          requestTimes.current = requestTimes.current.filter((x) => t - x < 5000);
          if (requestTimes.current.length > 30) {
            sendAlert("burst_requests", "high", { count: requestTimes.current.length, window_s: 5 });
            requestTimes.current = []; // reset
          }
        }
        return origFetch.apply(this, args);
      };
      wrapped.__blitzz_wrapped = true;
      window.fetch = wrapped;
    }

    // ── 4. Auto-paste sans interaction préalable ────────────────────
    const onAnyInteract = () => { lastInteraction.current = Date.now(); };
    const onPaste = (e) => {
      const sinceLast = Date.now() - lastInteraction.current;
      if (sinceLast > 3000) {
        sendAlert("auto_paste", "medium", { since_last_interaction_ms: sinceLast });
      }
    };
    window.addEventListener("mousedown", onAnyInteract);
    window.addEventListener("keydown", onAnyInteract);
    window.addEventListener("paste", onPaste);

    // ── 5. Mouvements de souris robotiques (variance temporelle = 0) ─
    // Iter 242 : seuils beaucoup plus stricts pour éviter les faux positifs
    // (trackpad / souris 60Hz régulière = comportement humain normal).
    // Exige 3 détections successives dans un cooldown de 60s avant alerte.
    const scrapeStrikes = { count: 0, lastStrikeAt: 0, lastAlertAt: 0 };
    const onMouseMove = (e) => {
      const t = Date.now();
      mouseTimings.current.push(t);
      mouseTimings.current = mouseTimings.current.slice(-50);
      if (mouseTimings.current.length === 50) {
        const deltas = [];
        for (let i = 1; i < mouseTimings.current.length; i++) {
          deltas.push(mouseTimings.current[i] - mouseTimings.current[i - 1]);
        }
        const mean = deltas.reduce((a, b) => a + b, 0) / deltas.length;
        const variance = deltas.reduce((a, b) => a + (b - mean) ** 2, 0) / deltas.length;
        // Seuils stricts : variance < 0.3 ET mean < 8ms (vraiment trop régulier pour un humain)
        // + cooldown 5min entre alertes + besoin de 3 strikes dans une fenêtre de 60s
        if (variance < 0.3 && mean < 8) {
          const now = Date.now();
          // Reset strikes si plus de 60s depuis la dernière
          if (now - scrapeStrikes.lastStrikeAt > 60000) {
            scrapeStrikes.count = 0;
          }
          scrapeStrikes.count += 1;
          scrapeStrikes.lastStrikeAt = now;
          // Alerte seulement si 3 strikes + 5min depuis la dernière alerte
          if (scrapeStrikes.count >= 3 && now - scrapeStrikes.lastAlertAt > 300000) {
            sendAlert("scrape_pattern", "high", { variance: Math.round(variance * 100) / 100, mean_ms: Math.round(mean), strikes: scrapeStrikes.count });
            scrapeStrikes.lastAlertAt = now;
            scrapeStrikes.count = 0;
          }
          mouseTimings.current = [];
        }
      }
    };
    window.addEventListener("mousemove", onMouseMove);

    // ── 6. Bot User-Agent self-check ────────────────────────────────
    const ua = navigator.userAgent || "";
    if (/HeadlessChrome|PhantomJS|Selenium|webdriver/i.test(ua) || navigator.webdriver === true) {
      sendAlert("bot_user_agent", "high", { ua: ua.substring(0, 200), webdriver: !!navigator.webdriver });
    }

    return () => {
      clearInterval(intDev);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("mousedown", onAnyInteract);
      window.removeEventListener("keydown", onAnyInteract);
      window.removeEventListener("paste", onPaste);
      window.removeEventListener("mousemove", onMouseMove);
    };
    /* eslint-disable-next-line */
  }, [user?.user_id]);

  if (!warning) return null;

  return (
    <div className="fixed top-3 left-1/2 -translate-x-1/2 z-50 max-w-md" data-testid="security-warning-banner">
      <div className="bg-rose-500/95 backdrop-blur text-white rounded-xl shadow-2xl p-3 flex items-start gap-2 border border-rose-300/30">
        <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="text-xs font-bold">Activité inhabituelle détectée</div>
          <div className="text-[11px] opacity-90 mt-0.5">
            Sécurité Blitzz : {warning.kind.replace(/_/g, " ")} ({warning.severity}). Cette session est journalisée.
          </div>
        </div>
        <button onClick={() => setWarning(null)} className="text-white/70 hover:text-white" data-testid="dismiss-security-warning">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}
